import { Fragment, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  Loader2, CalendarCheck, AlertTriangle, ChevronDown, ChevronUp, Save, RefreshCw, Info,
} from 'lucide-react'

// ==================== Types ====================

export interface ClosePoint {
  year_month: string
  period_end: string
  status: 'provisional' | 'closed'
  origin: 'auto' | 'manual' | 'reconstructed'
  total_assets: number
  total_liabilities: number
  net_value: number
  delta: number | null
  delta_pct: number | null
  warnings: string[]
  notes?: string | null
}

interface CloseChange {
  key: string
  label: string
  kind: 'asset' | 'liability'
  source?: string
  before: number
  after: number
  effect: number
}

interface CloseDetail extends ClosePoint {
  inputs: { bank_balance?: number; card_pending?: number }
  components: { key: string; label: string; kind: string; amount: number; detail?: string }[]
  comparison: {
    previous_year_month: string
    previous_net_value: number
    delta: number
    changes: CloseChange[]
    bridge: {
      profit: number | null
      profit_note: string | null
      owner_movements: number | null
      owner_detail: { date: string; concept: string; amount: number }[]
      manual_changes: number
      explained: number | null
      unexplained: number | null
    }
  } | null
}

// ==================== Helpers ====================

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre']

function fmtCur(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)
}

function fmtCurFull(v: number): string {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v)
}

function fmtSigned(v: number): string {
  return `${v > 0 ? '+' : ''}${fmtCurFull(v)}`
}

function monthShort(ym: string): string {
  const [y, m] = ym.split('-')
  return `${MONTHS[parseInt(m, 10) - 1].slice(0, 3)} ${y.slice(2)}`
}

function monthLong(ym: string): string {
  const [y, m] = ym.split('-')
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`
}

function deltaColor(v: number | null | undefined): string {
  if (v == null) return 'text-slate-400'
  return v > 0 ? 'text-green-600' : v < 0 ? 'text-red-600' : 'text-slate-500'
}

function StatusBadge({ c }: { c: ClosePoint }) {
  if (c.status === 'provisional') {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Provisional</span>
  }
  if (c.origin === 'reconstructed') {
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400" title={c.notes ?? undefined}>Reconstruido</span>
  }
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">Cerrado</span>
}

// ==================== Close input form ====================

function CloseInputs({ close, onSaved }: { close: ClosePoint; onSaved: () => void }) {
  const [bank, setBank] = useState('')
  const [card, setCard] = useState('')
  const mutation = useMutation({
    mutationFn: async (body: { bank_balance?: number; card_pending?: number }) =>
      (await api.put(`/business-value/closes/${close.year_month}/inputs`, body)).data,
    onSuccess: () => { setBank(''); setCard(''); onSaved() },
  })
  const submit = () => {
    const b = parseFloat(bank.replace(/\./g, '').replace(',', '.'))
    const c = parseFloat(card.replace(/\./g, '').replace(',', '.'))
    if (isNaN(b)) return
    mutation.mutate({ bank_balance: b, ...(isNaN(c) ? {} : { card_pending: Math.abs(c) }) })
  }
  const [y, m] = close.year_month.split('-')
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 mb-4">
      <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Falta el saldo del banco para cerrar {monthLong(close.year_month)}</div>
          <div className="mt-1 text-amber-700 dark:text-amber-300">
            Sube los extractos de <b>cuenta y tarjeta</b> a la carpeta de la asesoría en Drive
            ({y}/Trimestre/{MONTHS[parseInt(m, 10) - 1]}/Banco/) y el mes se cierra solo en unas horas.
            O escribe aquí el saldo a {close.period_end.split('-').reverse().join('/')}:
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2 sm:ml-6">
        <label className="text-xs text-amber-800 dark:text-amber-200">
          Saldo cuenta
          <input
            type="text" inputMode="decimal" placeholder="Ej: 57.177,02" value={bank}
            onChange={e => setBank(e.target.value)}
            className="block mt-1 w-36 px-2 py-1.5 text-sm text-right border border-amber-300 dark:border-amber-700 rounded bg-white dark:bg-slate-800"
          />
        </label>
        <label className="text-xs text-amber-800 dark:text-amber-200">
          Gasto tarjeta del mes (opcional)
          <input
            type="text" inputMode="decimal" placeholder="Se estima si lo dejas vacío" value={card}
            onChange={e => setCard(e.target.value)}
            className="block mt-1 w-52 px-2 py-1.5 text-sm text-right border border-amber-300 dark:border-amber-700 rounded bg-white dark:bg-slate-800"
          />
        </label>
        <button
          onClick={submit}
          disabled={mutation.isPending || !bank}
          className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded flex items-center gap-1.5">
          {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Cerrar el mes
        </button>
      </div>
      {mutation.isError && <div className="mt-2 text-xs text-red-600 sm:ml-6">No se pudo guardar: {(mutation.error as Error).message}</div>}
    </div>
  )
}

// ==================== Close detail ====================

function CloseDetailView({ ym }: { ym: string }) {
  const { data, isLoading } = useQuery<CloseDetail>({
    queryKey: ['business-value-close', ym],
    queryFn: async () => (await api.get(`/business-value/closes/${ym}`)).data,
  })
  if (isLoading || !data) {
    return <div className="py-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Cargando cierre…</div>
  }
  const cmp = data.comparison
  return (
    <div className="p-4 space-y-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg">
      {data.notes && <div className="text-xs text-slate-500 flex gap-1.5"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />{data.notes}</div>}
      {data.warnings.length > 0 && (
        <ul className="text-xs text-amber-700 dark:text-amber-300 list-disc ml-5">
          {data.warnings.map(w => <li key={w}>{w}</li>)}
        </ul>
      )}
      {!cmp ? (
        <div className="text-sm text-slate-500">Es el primer cierre: a partir del siguiente verás qué partidas explican cada variación.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">
              Qué ha cambiado desde {monthShort(cmp.previous_year_month)}
            </h4>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-1 pr-2 font-medium">Partida</th>
                  <th className="py-1 pr-2 font-medium text-right">{monthShort(cmp.previous_year_month)}</th>
                  <th className="py-1 pr-2 font-medium text-right">{monthShort(data.year_month)}</th>
                  <th className="py-1 font-medium text-right">Efecto en el valor</th>
                </tr>
              </thead>
              <tbody>
                {cmp.changes.filter(c => Math.abs(c.effect) >= 1).map(c => (
                  <tr key={c.key} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
                    <td className="py-1 pr-2 text-slate-600 dark:text-slate-300">{c.label}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-slate-500">{fmtCur(c.before)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-slate-500">{fmtCur(c.after)}</td>
                    <td className={`py-1 text-right tabular-nums font-medium ${deltaColor(c.effect)}`}>{fmtSigned(c.effect)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="pt-2 text-slate-700 dark:text-slate-200" colSpan={3}>Variación del valor neto</td>
                  <td className={`pt-2 text-right tabular-nums ${deltaColor(cmp.delta)}`}>{fmtSigned(cmp.delta)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <h4 className="text-xs font-semibold uppercase text-slate-500 mb-2">¿Cuadra con el beneficio del mes?</h4>
            <table className="w-full text-xs">
              <tbody>
                <tr className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="py-1 text-slate-600 dark:text-slate-300">Beneficio neto del mes (módulo financiero)</td>
                  <td className={`py-1 text-right tabular-nums ${deltaColor(cmp.bridge.profit)}`}>
                    {cmp.bridge.profit != null ? fmtSigned(cmp.bridge.profit) : <span className="text-slate-400">{cmp.bridge.profit_note}</span>}
                  </td>
                </tr>
                <tr className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="py-1 text-slate-600 dark:text-slate-300"
                    title={cmp.bridge.owner_detail.map(m => `${m.date}: ${m.concept} ${fmtCurFull(m.amount)}`).join('\n')}>
                    Aportaciones (+) / retiradas (−) del titular
                  </td>
                  <td className={`py-1 text-right tabular-nums ${deltaColor(cmp.bridge.owner_movements)}`}>
                    {cmp.bridge.owner_movements != null ? fmtSigned(cmp.bridge.owner_movements) : <span className="text-slate-400">sin extracto</span>}
                  </td>
                </tr>
                <tr className="border-b border-slate-100 dark:border-slate-700/50">
                  <td className="py-1 text-slate-600 dark:text-slate-300">Partidas manuales (préstamo, financiación familiar, inmuebles…)</td>
                  <td className={`py-1 text-right tabular-nums ${deltaColor(cmp.bridge.manual_changes)}`}>{fmtSigned(cmp.bridge.manual_changes)}</td>
                </tr>
                <tr className="border-b border-slate-200 dark:border-slate-700 font-medium">
                  <td className="py-1 text-slate-700 dark:text-slate-200">Variación explicada</td>
                  <td className="py-1 text-right tabular-nums">{cmp.bridge.explained != null ? fmtSigned(cmp.bridge.explained) : '—'}</td>
                </tr>
                <tr className="font-semibold">
                  <td className="pt-1.5 text-slate-700 dark:text-slate-200">Sin explicar</td>
                  <td className={`pt-1.5 text-right tabular-nums ${cmp.bridge.unexplained != null && Math.abs(cmp.bridge.unexplained) > Math.max(2000, Math.abs(cmp.delta) * 0.25) ? 'text-amber-600' : 'text-slate-600 dark:text-slate-300'}`}>
                    {cmp.bridge.unexplained != null ? fmtSigned(cmp.bridge.unexplained) : '—'}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-slate-400 leading-snug">
              Si el balance está completo, la variación del valor ≈ beneficio + aportaciones − retiradas.
              Lo "sin explicar" son partidas que aún no se miden bien (p. ej. IVA de un mes con ventas
              muy por encima de lo normal, revalorización del stock por cambios de coste) o un beneficio
              del mes incompleto.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================== Panel ====================

export default function MonthlyClosesPanel() {
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState<string | null>(null)
  const { data, isLoading } = useQuery<{ closes: ClosePoint[]; statements_until: string | null }>({
    queryKey: ['business-value-closes'],
    queryFn: async () => (await api.get('/business-value/closes')).data,
  })

  const cycleMutation = useMutation({
    mutationFn: async () => (await api.post('/business-value/closes-cycle')).data,
    onSuccess: () => invalidate(),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['business-value-closes'] })
    queryClient.invalidateQueries({ queryKey: ['business-value-close'] })
    queryClient.invalidateQueries({ queryKey: ['business-value-current'] })
  }

  const closes = data?.closes ?? []
  const provisional = [...closes].reverse().find(c => c.status === 'provisional')

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
            <CalendarCheck className="w-4 h-4 text-primary-600" />
            Cierres mensuales
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Una foto por mes con corte el último día. Se crea sola el día 1 y se cierra al tener el saldo del banco.
            {data?.statements_until && ` Extractos importados hasta ${data.statements_until.split('-').reverse().join('/')}.`}
          </p>
        </div>
        <button
          onClick={() => cycleMutation.mutate()}
          disabled={cycleMutation.isPending}
          title="Busca extractos nuevos y recalcula los cierres provisionales"
          className="px-2.5 py-1.5 text-xs font-medium border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <RefreshCw className={`w-3.5 h-3.5 ${cycleMutation.isPending ? 'animate-spin' : ''}`} />
          Buscar extractos
        </button>
      </div>

      {provisional && <CloseInputs close={provisional} onSaved={invalidate} />}

      {isLoading ? (
        <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : closes.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">
          Aún no hay cierres. El primero se creará automáticamente el día 1 del próximo mes.
        </div>
      ) : (
        <>
          {closes.length >= 2 && (
            <div className="h-60 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={closes.map(c => ({ name: monthShort(c.year_month), net: c.net_value, status: c.status, origin: c.origin }))}>
                  <defs>
                    <linearGradient id="gradCloses" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmtCur(v)} width={80} domain={['auto', 'auto']} />
                  <RechartsTooltip
                    formatter={(value: number | undefined) => [fmtCurFull(value ?? 0), 'Valor neto']}
                    labelFormatter={(label, payload) => {
                      const p = payload?.[0]?.payload
                      if (!p) return label
                      const tag = p.status === 'provisional' ? ' · provisional' : p.origin === 'reconstructed' ? ' · reconstruido' : ''
                      return `${label}${tag}`
                    }}
                  />
                  <Area
                    type="monotone" dataKey="net" stroke="#3b82f6" fill="url(#gradCloses)" strokeWidth={2}
                    dot={(props: { cx?: number; cy?: number; payload?: { status: string }; index?: number }) => (
                      <circle
                        key={props.index} cx={props.cx} cy={props.cy} r={4}
                        fill={props.payload?.status === 'provisional' ? '#fff' : '#3b82f6'}
                        stroke={props.payload?.status === 'provisional' ? '#d97706' : '#3b82f6'} strokeWidth={2}
                      />
                    )}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase border-b border-slate-200 dark:border-slate-700">
                  <th className="py-2 pr-4">Mes</th>
                  <th className="py-2 pr-4 text-right">Activos</th>
                  <th className="py-2 pr-4 text-right">Deudas</th>
                  <th className="py-2 pr-4 text-right">Valor neto</th>
                  <th className="py-2 pr-4 text-right">Variación</th>
                  <th className="py-2 pr-4 text-right">%</th>
                  <th className="py-2 pr-2">Estado</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {[...closes].reverse().map(c => (
                  <Fragment key={c.year_month}>
                    <tr
                      onClick={() => setExpanded(expanded === c.year_month ? null : c.year_month)}
                      className="border-b border-slate-100 dark:border-slate-700/50 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-2 pr-4 font-medium text-slate-700 dark:text-slate-200 capitalize whitespace-nowrap">{monthLong(c.year_month)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{fmtCur(c.total_assets)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-red-600 dark:text-red-400">{fmtCur(-c.total_liabilities)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums font-semibold">{fmtCurFull(c.net_value)}</td>
                      <td className={`py-2 pr-4 text-right tabular-nums ${deltaColor(c.delta)}`}>{c.delta == null ? '—' : fmtSigned(c.delta)}</td>
                      <td className={`py-2 pr-4 text-right tabular-nums ${deltaColor(c.delta_pct)}`}>
                        {c.delta_pct == null ? '—' : `${c.delta_pct > 0 ? '+' : ''}${c.delta_pct.toFixed(1)}%`}
                      </td>
                      <td className="py-2 pr-2"><StatusBadge c={c} /></td>
                      <td className="py-2 text-slate-400">
                        {expanded === c.year_month ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </td>
                    </tr>
                    {expanded === c.year_month && (
                      <tr>
                        <td colSpan={8} className="py-2"><CloseDetailView ym={c.year_month} /></td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
